import { getMultiCartItems } from 'api/cart/getMultiCartItems'
import { getMultiCartSummaries } from 'api/cart/getMultiCartSummaries'
import { getMultiCartMembers } from 'api/cart/getMultiCartMembers'
import { assertEvent, assign, DoneActorEvent, ErrorActorEvent, fromPromise, log, setup } from 'xstate'
import { inviteUsersToMultiCart, MultiCartInvitationUsers } from 'api/cart/inviteUsersToMultiCart'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { createMultiCart } from 'api/cart/createMultiCart'
import { showErrorSnackbar, showSuccessSnackbar } from './SnackbarActions'
import { History } from 'history'
import { joinUserToMultiCart } from 'api/cart/joinUserToMultiCart'
import { buildUrl } from 'lib/url/searchUrlUtils'
import { deleteMultiCart } from 'api/cart/deleteMultiCart'
import { updateMultiCartName } from 'api/cart/updateMultiCartName'
import { leaveMultiCart } from 'api/cart/leaveMultiCart'
import { revokeUserAccessMultiCart } from 'api/cart/revokeUserAccessMultiCart'

type MultiCartMachineInput = {
  memberId?: string
  history: History<unknown>
  currentRegionCode: string
}

type MultiCartMachineContext = {
  cartSummary: { status: 'loaded', data: MachineContext.CartSummary } | { status: 'error', error: string } | { status: 'loading' }
  cartItems: { status: 'loaded', data: MachineContext.CartItems } | { status: 'error', error: string } | { status: 'loading' }
  cartMembers: { status: 'loaded', data: MachineContext.CartMembers } | { status: 'error', error: string } | { status: 'loading' }
  activeCartId?: string
  cartBeingCreated?: App.MultiCartSummary
  // Redux state from input
  memberId?: string
  history: History<unknown>
  currentRegionCode: string
}

type InviteMultiCartUsers = {
  type: 'invite multi cart users'
  cartId: string
  users: MultiCartInvitationUsers
}

type CreateMultiCart = {
  type: 'create multi cart'
  cartName: string
}

type DeleteMultiCart = {
  type: 'delete multi cart'
  cartId: string
}

type UpdateMultiCart = {
  type: 'update multi cart'
  cartId: string
  name: string
}

type LeaveMultiCart = {
  type: 'leave multi cart'
  cartId: string
}

type RevokeUserAccess = {
  type: 'revoke user access'
  cartId: string
  userId: string
}

type SetActiveCart = {
  type: 'set active cart'
  cartId: string
}

type SetDefaultActiveCart = {
  type: 'set default active cart'
}

type ClearCartBeingCreated = {
  type: 'clear cart being created'
}

type MultiCartMachineEvents = {
  type: 'set active cart'
  cartId: string
} | {
  type: 'successful login'
} | {
  type: 'failed login'
} | {
  type: 'join multi cart'
  sharedCartId: string
} | InviteMultiCartUsers | CreateMultiCart | DeleteMultiCart | UpdateMultiCart | LeaveMultiCart | RevokeUserAccess | SetActiveCart | SetDefaultActiveCart | ClearCartBeingCreated
| DoneActorEvent<App.MultiCartSummary, string>
| DoneActorEvent<{ cartId: string, user: App.MultiCartMember }, string>
| DoneActorEvent<MachineContext.CartSummary, string>
| DoneActorEvent<Array<MachineContext.CartItems>, string>
| DoneActorEvent<Array<MachineContext.CartMembers>, string>
| ErrorActorEvent<MachineContext.CartSummary, string>
| ErrorActorEvent<Array<MachineContext.CartItems>, string>
| ErrorActorEvent<Array<MachineContext.CartMembers>, string>

/**
 * State machine docs - https://stately.ai/docs/state-machines-and-statecharts
 */

const multiCartMachine = setup({
  types: {
    context: {} as MultiCartMachineContext,
    events: {} as MultiCartMachineEvents,
    input: {} as MultiCartMachineInput,
  },
  actors: {
    createMultiCart: fromPromise(({ input }: { input: { cartName: string } }) => createMultiCart(input.cartName)),
    updateMultiCartName: fromPromise(({ input }: { input: { cartId: string, name: string } }) => updateMultiCartName(input.cartId, input.name)),
    deleteMultiCart: fromPromise(({ input }: { input: { cartId: string } }) => deleteMultiCart(input.cartId)),
    leaveMultiCart: fromPromise(({ input }: { input: { cartId: string } }) => leaveMultiCart(input.cartId)),
    getMultiCarts: fromPromise(getMultiCartSummaries),
    getMultiCartItems: fromPromise(({ input }: { input: Array<string> }) => Promise.all(input.map(getMultiCartItems))),
    getMultiCartMembers: fromPromise(({ input }: { input: Array<string> }) => Promise.all(input.map(getMultiCartMembers))),
    inviteUsersToMultiCart: fromPromise(({ input }: { input: { cartId: string, users: MultiCartInvitationUsers } }) => inviteUsersToMultiCart(input.cartId, input.users)),
    joinUserToMultiCart: fromPromise(({ input }: { input: { sharedCartId: string } }) => joinUserToMultiCart(input.sharedCartId)),
    revokeUserAccessMultiCart: fromPromise(({ input }: { input: { cartId: string, userId: string } }) => revokeUserAccessMultiCart(input.cartId, input.userId)),
  },
  actions: {
    assignCartSummary: assign({
      cartSummary: ({ event }: { event: DoneActorEvent<MachineContext.CartSummary, string> }) => ({
        status: 'loaded' as const,
        data: event.output,
      })
    }),
    assignCartSummaryFetchError: assign({
      cartSummary: () => ({
        status: 'error' as const,
        error: 'Failed to load cart summary'
      })
    }),
    assignCartItems: assign({
      cartItems: ({ event }: { event: DoneActorEvent<Array<MachineContext.CartItems>, string> }) => {
        // Merge all cart items from Promise.all results into a single object
        return {
          status: 'loaded' as const,
          data: event.output.reduce((acc, cartItems) => ({ ...acc, ...cartItems }), {} as MachineContext.CartItems)
        }
      }
    }),
    appendCartItems: assign({
      cartItems: ({ event, context }: { event: DoneActorEvent<Array<MachineContext.CartItems>, string>, context: MultiCartMachineContext }) => {
        const newCartItems = event.output.reduce((acc, cartItems) => ({ ...acc, ...cartItems }), {} as MachineContext.CartItems)

        return {
          status: 'loaded' as const,
          data: {
            ...context.cartItems.status === 'loaded' ? context.cartItems.data : {},
            ...newCartItems,
          }
        }
      }
    }),
    appendCartMembers: assign({
      cartMembers: ({ event, context }: { event: DoneActorEvent<Array<MachineContext.CartMembers>, string>, context: MultiCartMachineContext }) => {
        const newCartMembers = event.output.reduce((acc, cartMembers) => ({ ...acc, ...cartMembers }), {} as MachineContext.CartMembers)
        return {
          status: 'loaded' as const,
          data: {
            ...context.cartMembers.status === 'loaded' ? context.cartMembers.data : {},
            ...newCartMembers,
          }
        }
      }
    }),
    assignCartItemsFetchError: assign({
      cartItems: () => ({
        status: 'error' as const,
        error: 'Failed to load cart items'
      })
    }),
    assignCartMembers: assign({
      cartMembers: ({ event }: { event: DoneActorEvent<Array<MachineContext.CartMembers>, string> }) => {
        // Merge all cart members from Promise.all results into a single object
        return {
          status: 'loaded' as const,
          data: event.output.reduce((acc, cartMembers) => ({ ...acc, ...cartMembers }), {} as MachineContext.CartMembers)
        }
      }
    }),
    assignCartMembersFetchError: assign({
      cartMembers: () => ({
        status: 'error' as const,
        error: 'Failed to load cart members'
      })
    }),
    assignCartBeingCreated: assign({
      cartBeingCreated: ({ event }: { event: DoneActorEvent<App.MultiCartSummary, string> }) => event.output,
      activeCartId: ({ event }: { event: DoneActorEvent<App.MultiCartSummary, string> }) => event.output.cartId,
      cartSummary: ({ event, context }: { event: DoneActorEvent<App.MultiCartSummary, string>, context: MultiCartMachineContext }) => {
        return {
          status: 'loaded' as const,
          data: {
            ...context.cartSummary.status === 'loaded' ? context.cartSummary.data : {},
            [event.output.cartId]: event.output,
          }
        }
      }
    }),
    assignActiveCartId: assign({
      activeCartId: ({ event }: { event: SetActiveCart }) => event.cartId,
    }),
    assignActiveCartToDefault: assign({
      activeCartId: ({ context }: { context: MultiCartMachineContext }) => {
        if (context.cartSummary.status === 'loaded') {
          const defaultCart = Object.values(context.cartSummary.data).find(cart => cart.type === 'default')
          return defaultCart?.cartId
        }
        return context.activeCartId
      },
    }),
    assignRevokedUserAccess: assign({
      cartMembers: ({ event, context }: { event: DoneActorEvent<{ cartId: string, user: App.MultiCartMember }, string>, context: MultiCartMachineContext }) => {
        if (context.cartMembers.status !== 'loaded' || !event.output.user.userId) {
          return context.cartMembers
        }
        return {
          status: 'loaded' as const,
          data: {
            ...context.cartMembers.data,
            [event.output.cartId]: context.cartMembers.data[event.output.cartId].map(member =>
              member.userId === event.output.user.userId
                ? event.output.user
                : member
            )
          }
        }
      }
    }),
    assignUpdatedCartSummary: assign({
      cartSummary: ({ event, context }: { event: DoneActorEvent<App.MultiCartSummary, string>, context: MultiCartMachineContext }) => {
        if (context.cartSummary.status !== 'loaded') {
          return context.cartSummary
        }

        return {
          ...context.cartSummary,
          data: {
            ...context.cartSummary.data,
            [event.output.cartId]: {
              ...context.cartSummary.data[event.output.cartId],
              name: event.output.name,
            }
          }
        }
      }
    }),
    clearCartBeingCreated: assign({
      cartBeingCreated: () => undefined,
    }),

    showInvitationSuccessSnackbar: () => {
      showSnackbar('Invitations sent!', 'success')
    },
    showInvitationErrorSnackbar: () => {
      showSnackbar('Failed to send invitations', 'critical')
    },
    showJoinCartSuccessSnackbar: () => {
      showSnackbar('You\'ve successfully joined the cart', 'success')
    },
    showJoinCartErrorSnackbar: () => {
      showSnackbar('Failed to join the cart', 'critical')
    },
    showDeleteCartSuccessSnackbar: () => {
      showSnackbar('Cart has been deleted!', 'success')
    },
    showDeleteCartErrorSnackbar: () => {
      showSnackbar('Failed to delete cart', 'critical')
    },
    showUpdateCartSuccessSnackbar: () => {
      showSnackbar('Cart has been updated!', 'success')
    },
    showUpdateCartErrorSnackbar: () => {
      showSnackbar('Failed to update cart', 'critical')
    },
    showLeaveCartSuccessSnackbar: () => {
      showSnackbar('You have left the cart!', 'success')
    },
    showLeaveCartErrorSnackbar: () => {
      showSnackbar('Failed to leave cart', 'critical')
    },
    showRevokeUserAccessSuccessSnackbar: () => {
      showSnackbar('User access has been revoked!', 'success')
    },
    showRevokeUserAccessErrorSnackbar: () => {
      showSnackbar('Failed to revoke user access', 'critical')
    },
    pushToCartPage: ({ context }) => {
      context.history.push(buildUrl('/cart', context.currentRegionCode))
    },
    logErrorEvent: log(({ event }: {
      event: ErrorActorEvent<MachineContext.CartSummary, string> | ErrorActorEvent<Array<MachineContext.CartItems>, string> | ErrorActorEvent<Array<MachineContext.CartMembers>, string>
    }) => `assignCartSummaryFetchError ${JSON.stringify(event.error)}`),
  },
  guards: {
    isUserLoggedIn: ({ context }) => !!context.memberId,
  },
}
).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QFsCuAbALgSwMIEMAnTAWXwGMALbAOzADoqxyBrWqAZU30zAGIA2gAYAuolAAHAPaxsOKTXEgAHogBMagKz0AnJoDMARn1qALGoDsANjUAOW2oA0IAJ7r9O+ptNCh+-RammkKaagEAvuHOaFh4RKQU1HSMlMxsNJzcvIKGYkgg0rLyivmqCGqGnqa2pvq2FkI6OqaBxs5u5RZVQlYeFpqGWk1qVpHRGDgExGRUtAxMrOxcPPwCanmSMnLYCkplALTG2rY6an6awZWGBlbtiIaGNvQWNaZWhvVC5o9jIDGT8RmSQYtG2+HQ2AAXuw+BAFAxYFkGP84tNEnN6KCcOCoexhBsCltint7j0hM8hNZauYNN5NHcEI8TPQPN59MF-JT9L8UVMErNkljsDjoRl6OgpPgIOw+RxUMhkEQXLD4ZiaAA3KQsZETVH84FqsEQ0VQcWS6UZWXyxWEFwIWia8g8HY0fH4pSFba7UqIfraMIXerWHSGUy1BmaCwWXRh2wBHRCGoWOo83V8oEYoUi9hmqUy+JyhVKvhgQiEKSEegSdA8ABmFeQ9F5gPRgpoRtxYolectBetSvtGqkTuKbtEHqJLpJCBDanoYd8FzUIf0NkMDKCVnoGhMQyjIT6qdi6dbIPb2ONOe7FqgfIAImBuNh0LAVclESsm2mWwKzx2TbmN73o++DPrA7r5J6xI+oyWhbmGzRfLYxhGE4rj3Jy25aKGNhdCcQhqEeAJor+hoXp2prXvmxAPk+L6AdRmAAJK8MgsAMRkb5npq2pfseP4Glml5duajG0aB9FUb2xAsWAbEcVAg6Os6ChjgSUFTjBFiWPQlRBB4OgBD4Oi3OhM4OPOvh6NUia2FYkZEXqGZtv+V6idJmDiWBCl8rJ8lSVAJZlhWVY1pg9aEI2zYkYJ57CsJlHube8ReZJSW+ax7EBUpw4qa6ogQZsRSaaAZTadGekGE0RmNKZHSGJS2jsj43xWO8XKOSepFCRRPkpSB3kBXyACqsClllSVcWqPE6vxMWZnF2YiT2yU0QNaUrSNY2EBNK05SOLpqROxXeqVvoXLoOiBEY7w6PYXwMsYjxeOYPgGHUZw6J1AkLa5y1Af1dG7QDxCjeNClBeWlbVnWDZ8cR+q-eRAFDYDEnA4xYM7Qp+15UdkGTqdKjndoTTXY8lT3aYj3GfO1mnFY1nBKMUR-N+83JIQYBSsqsCoOQ5BwLAtYYAABBKUC0IVhInSUZ0IA4EbLl4BkNK9XSWN9HMMFzPN8LWEmQOLUiS-l6mE3LxMIFYtjkpYib6G8ARfFYFiPYzhi6IE9hWLUDQoZErM0FIEBwEo0WI3Qx1epbBwfPo9AnGc7KXCGNyPQEzwGaYV2cjYTRa5H8ypIsGTLLw0fQfL5gMrS27Z7n+g9MuX2sxHzl-sj7CVyVVtGJ4Nt3TbjtHL0DKrrYzxhBUCHeBU1iFx3ZHxb1qPEIWNodEVMfTqu0aD3ZcamKP+gbno9C+4MIQOIYgRfIvp7L0tiWbWjYE90TZR1JPbxCLfJyzlOGhDottow+ETMmAIDxdwP26otBKfU1pA0QcxTKH9Y6IFqPBKwf8XhNEGEAhkJxTC6F8PYaooQqTcjbuzIuT8EFr08utDGHk-IsKgOg6c1QSG-3-vg5cGgGRNBIUGFoN8vhGE0LA2Kf0X4gyYcgxhbD6BwijgTWW04TJzlCCMGkzRNAOHpGZBqBFE5RgGNSHobxW7jDmnQnqKN0pvw2vI5RpYoacJglorwGhfYaH0YYx6jRjhNxwY8K69hkLSKRivRxr8kHoxQVjeA6id4wTDJ7Ju+C2TxmpsY-x9AvhmFqMhYhUiaF2KXg4ty8SFGJMYckhSnjq4nBZI0NOtRckZ2Vn-JO9Q+jXWiS5Lu-0xLMKSdtdiqiwDNKtiY8k9l2QmRCNpGwtgM6Z16SZaojM7p3SGZ3WJNT5GpXYVtcG7iKyzLKPMi+BhNDLMjCMOwNNlZXTODoywYRQwHPoavc0kBrn3AeZ4F4jNExrLOL7bpnswj9BMiYAxADfnVLFJcwgQLYLVB8X6R4tRfAPTMjnTw0CcEHz8KuX5usIBbxlmk+WyysI+BwXUMM9zHoOATl0BqGgHDJl+bASgRBID3mwLAfAAAjdAgLUlVytshaMSEbaWFKXZPJHQwhbi+L4W+hlbZxh+YHIAA */
  id: 'multiCartMachine',
  context: ({ input }: { input: MultiCartMachineInput }) => ({
    cartSummary: {
      status: 'loading' as const,
    },
    cartItems: {
      status: 'loading' as const,
    },
    cartMembers: {
      status: 'loading' as const,
    },
    // Initialize with Redux values
    memberId: input.memberId,
    history: input.history,
    currentRegionCode: input.currentRegionCode,
  }),
  // Determine initial state - no always transitions at the top level to avoid recursion
  initial: 'checkingState',
  states: {
    checkingState: {
      always: [
        {
          target: 'initializing',
          guard: 'isUserLoggedIn'
        },
        {
          target: 'ready'
        }
      ]
    },
    initializing: {
      initial: 'loadingCartSummary',
      states: {
        loadingCartSummary: {
          invoke: {
            src: 'getMultiCarts',
            onDone: {
              target: 'loadingCartDetails',
              actions: ['assignCartSummary']
            },
            onError: {
              target: 'error',
              actions: ['logErrorEvent', 'assignCartSummaryFetchError']
            },
          }
        },
        loadingCartDetails: {
          type: 'parallel',
          onDone: {
            target: 'loaded'
          },
          states: {
            loadingCartItems: {
              initial: 'loading',
              states: {
                loading: {
                  invoke: {
                    src: 'getMultiCartItems',
                    input: ({ context }) => context.cartSummary.status === 'loaded' && context.cartSummary.data ? Object.keys(context.cartSummary.data) : [],
                    onDone: {
                      target: 'done',
                      actions: 'assignCartItems'
                    },
                    onError: {
                      target: 'error',
                      actions: 'assignCartItemsFetchError'
                    },
                  }
                },
                done: {
                  type: 'final',
                },
                error: {
                  type: 'final',
                },
              }
            },
            loadingCartUsers: {
              initial: 'loading',
              states: {
                loading: {
                  invoke: {
                    src: 'getMultiCartMembers',
                    input: ({ context }) => context.cartSummary.status === 'loaded' && context.cartSummary.data ? Object.keys(context.cartSummary.data) : [],
                    onDone: {
                      target: 'done',
                      actions: 'assignCartMembers'
                    },
                    onError: {
                      target: 'error',
                      actions: 'assignCartMembersFetchError'
                    },
                  }
                },
                done: {
                  type: 'final',
                },
                error: {
                  type: 'final',
                },
              }
            },
          }
        },
        loaded: {
          type: 'final'
        },
        error: {
          type: 'final',
        },
      },
      onDone: {
        target: 'ready',
      }
    },
    ready: {
      on: {
        'successful login': {
          target: 'checkingState',
        },
        'failed login': {
          target: 'ready',
        },
        'invite multi cart users': {
          target: 'inviting',
        },
        'create multi cart': {
          target: 'creatingMultiCart',
        },
        'delete multi cart': {
          target: 'deletingMultiCart',
        },
        'update multi cart': {
          target: 'updatingMultiCart',
        },
        'leave multi cart': {
          target: 'leavingMultiCart',
        },
        'revoke user access': {
          target: 'revokingUserAccess',
        },
        'set active cart': {
          target: 'ready',
          actions: 'assignActiveCartId'
        },
        'set default active cart': {
          target: 'ready',
          actions: 'assignActiveCartToDefault'
        },
        'join multi cart': {
          target: 'joiningUserToMultiCart',
        },
        'clear cart being created': {
          actions: 'clearCartBeingCreated'
        },
      },
    },
    creatingMultiCart: {
      invoke: {
        src: 'createMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'create multi cart')
          return {
            cartName: event.cartName,
          }
        },
        onDone: {
          target: 'fetchingActiveCartItems',
          actions: [({ event }) => {
            showSuccessSnackbar(`${event.output.name} cart has been created!`)
          }, 'assignCartBeingCreated']
        },
        onError: {
          target: 'ready',
          actions: [() => {
            showErrorSnackbar('Failed to create cart')
          }]
        },
      },
      on: {
        'clear cart being created': {
          target: 'ready',
          actions: 'clearCartBeingCreated'
        },
      },
    },
    updatingMultiCart: {
      invoke: {
        src: 'updateMultiCartName',
        input: ({ event }) => {
          assertEvent(event, 'update multi cart')
          return {
            cartId: event.cartId,
            name: event.name,
          }
        },
        onDone: {
          target: 'ready',
          actions: ['showUpdateCartSuccessSnackbar', 'assignUpdatedCartSummary']
        },
        onError: {
          target: 'ready',
          actions: 'showUpdateCartErrorSnackbar'
        },
      },
    },
    leavingMultiCart: {
      invoke: {
        src: 'leaveMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'leave multi cart')
          return {
            cartId: event.cartId,
          }
        },
        onDone: {
          target: 'initializing',
          actions: ['assignActiveCartToDefault', 'showLeaveCartSuccessSnackbar']
        },
        onError: {
          target: 'ready',
          actions: 'showLeaveCartErrorSnackbar'
        },
      },
    },
    revokingUserAccess: {
      invoke: {
        src: 'revokeUserAccessMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'revoke user access')
          return {
            cartId: event.cartId,
            userId: event.userId,
          }
        },
        onDone: {
          target: 'ready',
          actions: ['showRevokeUserAccessSuccessSnackbar', 'assignRevokedUserAccess']
        },
        onError: {
          target: 'ready',
          actions: 'showRevokeUserAccessErrorSnackbar'
        },
      },
    },
    deletingMultiCart: {
      invoke: {
        src: 'deleteMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'delete multi cart')
          return {
            cartId: event.cartId,
          }
        },
        onDone: {
          target: 'initializing',
          actions: ['assignActiveCartToDefault', 'showDeleteCartSuccessSnackbar']
        },
        onError: {
          target: 'ready',
          actions: 'showDeleteCartErrorSnackbar'
        },
      },
    },
    inviting: {
      invoke: {
        src: 'inviteUsersToMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'invite multi cart users')
          return {
            cartId: event.cartId,
            users: event.users,
          }
        },
        onDone: {
          target: 'fetchingNewCartMembers',
          actions: 'showInvitationSuccessSnackbar'
        },
        onError: {
          target: 'ready',
          actions: 'showInvitationErrorSnackbar'
        },
      },
      on: {
        'clear cart being created': {
          target: 'ready',
          actions: 'clearCartBeingCreated'
        },
      },
    },
    fetchingActiveCartItems: {
      invoke: {
        src: 'getMultiCartItems',
        input: ({ context }) => [context.activeCartId!],
        onDone: {
          target: 'fetchingNewCartMembers',
          actions: 'appendCartItems'
        },
        onError: {
          target: 'ready',
          actions: 'assignCartItemsFetchError',
        },
      },
      on: {
        'clear cart being created': {
          target: 'ready',
          actions: 'clearCartBeingCreated'
        },
      },
    },
    fetchingNewCartMembers: {
      invoke: {
        src: 'getMultiCartMembers',
        input: ({ context }) => [context.activeCartId!],
        onDone: {
          target: 'ready',
          actions: 'appendCartMembers'
        },
        onError: {
          target: 'ready',
          actions: 'assignCartMembersFetchError',
        },
      },
      on: {
        'clear cart being created': {
          target: 'ready',
          actions: 'clearCartBeingCreated'
        },
      },
    },
    joiningUserToMultiCart: {
      invoke: {
        src: 'joinUserToMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'join multi cart')
          return {
            sharedCartId: event.sharedCartId,
          }
        },
        onDone: {
          /* TODO: create a state to fetch single cart items and members */
          target: 'initializing',
          actions: ['showJoinCartSuccessSnackbar', 'pushToCartPage'],
        },
        onError: {
          target: 'ready',
          actions: 'showJoinCartErrorSnackbar',
        },
      },
    },
  },
})

export default multiCartMachine
